/* MAQUETA VISUAL (datos de ejemplo) — panel de inicio del estudiante.
   Elección de la dueña: "la opción A" (tarjetas de acceso), pero solo las tres
   primeras (WSS Live, Conjugación, Recursos), con una línea de fotografías y
   un solo artículo del muro abajo. Dos acabados para que elija el estilo.
   No consulta la API; se convierte en el panel real tras su elección. */
import {
  ArrowRight, ArrowUpRight, Radio, Sparkles, BookOpen, Camera,
  MessageCircle, Pin, Play, Clock,
} from "lucide-react";

const FOTOS = [
  { url: "https://images.unsplash.com/photo-1581726690015-c9861fa5057f?crop=entropy&cs=srgb&fm=jpg&q=85&w=640", cap: "Clase de A2 · martes" },
  { url: "https://images.unsplash.com/photo-1720347274787-961f33ef151d?crop=entropy&cs=srgb&fm=jpg&q=85&w=640", cap: "Taller de conversación" },
  { url: "https://images.unsplash.com/photo-1536709017021-ce8f99c17e38?crop=entropy&cs=srgb&fm=jpg&q=85&w=640", cap: "Paseo a Manuel Antonio" },
  { url: "https://images.unsplash.com/photo-1571260899304-425eee4c7efc?crop=entropy&cs=srgb&fm=jpg&q=85&w=640", cap: "Grupo de los jueves" },
  { url: "https://images.unsplash.com/photo-1620658927695-c33df6fb8130?crop=entropy&cs=srgb&fm=jpg&q=85&w=640", cap: "Río Celeste con la clase" },
  { url: "https://images.unsplash.com/photo-1577896851231-70ef18881754?crop=entropy&cs=srgb&fm=jpg&q=85&w=640", cap: "Profe Marcela" },
];

const Etiqueta = ({ children, className = "" }) => (
  <div className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${className}`}>{children}</div>
);

const Bloque = ({ titulo, nota, children, testId }) => (
  <section className="mb-16" data-testid={testId}>
    <div className="mb-6 flex items-baseline gap-3 flex-wrap">
      <h2 className="font-display text-lg font-bold tracking-tight text-slate-900">{titulo}</h2>
      <p className="text-sm text-slate-500">{nota}</p>
    </div>
    {children}
  </section>
);

/* ───────────── ESTILO 1 · tarjetas de color, bien llamativas ───────────── */
const TARJETAS_COLOR = [
  {
    key: "live",
    icon: Radio,
    label: "WSS Live",
    sub: "Clases en vivo todos los días, entrás cuando querás",
    cta: "Ver las clases de hoy",
    base: "bg-[#004aad]",
    blob1: "bg-white/10",
    blob2: "bg-orange-400/30",
    pill: "Hoy a las 5:00 p.m.",
  },
  {
    key: "conj",
    icon: Sparkles,
    label: "Conjugación",
    sub: "Entrená los verbos a tu ritmo, como un juego",
    cta: "Practicar ahora",
    base: "bg-[#ff731d]",
    blob1: "bg-white/15",
    blob2: "bg-amber-200/40",
    pill: "7 días de racha",
  },
  {
    key: "rec",
    icon: BookOpen,
    label: "Recursos",
    sub: "Guías, audios y todo el material de la escuela",
    cta: "Abrir la biblioteca",
    base: "bg-[#0f766e]",
    blob1: "bg-white/10",
    blob2: "bg-emerald-300/30",
    pill: "48 materiales",
  },
];

const Estilo1 = () => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="preview-estilo-color">
    {TARJETAS_COLOR.map((c) => (
      <button key={c.key}
              className={`group relative overflow-hidden rounded-[28px] ${c.base} text-left text-white p-7 min-h-[260px] flex flex-col justify-between transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-slate-900/15`}>
        <span className={`pointer-events-none absolute -right-10 -top-14 h-44 w-44 rounded-full ${c.blob1} transition-transform duration-500 group-hover:scale-125`} />
        <span className={`pointer-events-none absolute -right-6 -bottom-16 h-36 w-36 rounded-full ${c.blob2} blur-[2px] transition-transform duration-500 group-hover:scale-110`} />
        <span className="pointer-events-none absolute inset-0 opacity-[0.12]"
              style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)", backgroundSize: "20px 20px" }} />
        <div className="relative">
          <span className="h-14 w-14 rounded-2xl grid place-items-center bg-white/15 backdrop-blur-sm ring-1 ring-white/25">
            <c.icon className="h-6 w-6" />
          </span>
          <div className="mt-6 font-display text-2xl font-bold tracking-tight">{c.label}</div>
          <p className="mt-1.5 text-sm text-white/80 leading-relaxed max-w-[90%]">{c.sub}</p>
        </div>
        <div className="relative mt-7 flex items-center justify-between gap-3">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-white/15 ring-1 ring-white/20">
            <Clock className="h-3 w-3" /> {c.pill}
          </span>
          <span className="inline-flex items-center gap-1.5 text-[13px] font-semibold">
            {c.cta}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
          </span>
        </div>
      </button>
    ))}
  </div>
);

/* ──────── ESTILO 2 · tarjetas blancas con foto y acento de color ──────── */
const TARJETAS_FOTO = [
  { key: "live", icon: Radio, label: "WSS Live", sub: "Clases en vivo todos los días", pill: "Hoy 5:00 p.m.",
    img: FOTOS[1].url, acento: "text-[#004aad]", chip: "bg-blue-50 text-blue-700", barra: "bg-[#004aad]" },
  { key: "conj", icon: Sparkles, label: "Conjugación", sub: "Entrená los verbos a tu ritmo", pill: "7 días de racha",
    img: FOTOS[5].url, acento: "text-[#ff731d]", chip: "bg-orange-50 text-orange-700", barra: "bg-[#ff731d]" },
  { key: "rec", icon: BookOpen, label: "Recursos", sub: "Guías, audios y material de clase", pill: "48 materiales",
    img: FOTOS[3].url, acento: "text-teal-700", chip: "bg-teal-50 text-teal-700", barra: "bg-teal-600" },
];

const Estilo2 = () => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="preview-estilo-foto">
    {TARJETAS_FOTO.map((c) => (
      <button key={c.key}
              className="group text-left bg-white rounded-[28px] border border-slate-200 overflow-hidden transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-slate-900/5 hover:border-slate-300">
        <div className="relative h-40 overflow-hidden">
          <img src={c.img} alt="" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
          <span className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-white to-transparent" />
        </div>
        <div className="px-6 pb-6 relative">
          <span className={`-mt-8 relative inline-grid h-14 w-14 place-items-center rounded-2xl bg-white shadow-md ring-1 ring-slate-100 ${c.acento}`}>
            <c.icon className="h-6 w-6" />
          </span>
          <div className="mt-4 flex items-start justify-between gap-2">
            <div className="font-display text-xl font-bold tracking-tight text-slate-900">{c.label}</div>
            <ArrowUpRight className={`h-5 w-5 text-slate-300 transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 ${`group-hover:${c.acento}`}`} />
          </div>
          <p className="text-sm text-slate-500 mt-1">{c.sub}</p>
          <span className={`mt-4 inline-block text-[11px] font-semibold px-2.5 py-1 rounded-full ${c.chip}`}>{c.pill}</span>
        </div>
        <div className={`h-1 w-0 ${c.barra} transition-all duration-300 group-hover:w-full`} />
      </button>
    ))}
  </div>
);

/* ─────────────────── Línea de fotografías (común) ─────────────────── */
const LineaFotos = () => (
  <div data-testid="preview-linea-fotos">
    <div className="flex items-end justify-between mb-4">
      <div className="flex items-center gap-2.5">
        <span className="h-9 w-9 rounded-xl grid place-items-center bg-slate-900 text-white"><Camera className="h-4 w-4" /></span>
        <div>
          <div className="font-display text-lg font-bold tracking-tight text-slate-900">Fotos de la semana</div>
          <div className="text-xs text-slate-500">Lo que pasó en la escuela</div>
        </div>
      </div>
      <button className="text-[13px] font-semibold text-[#004aad] inline-flex items-center gap-1.5 hover:gap-2.5 transition-all">
        Ver la galería <ArrowRight className="h-3.5 w-3.5" />
      </button>
    </div>
    <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x">
      {FOTOS.map((f, i) => (
        <figure key={f.url}
                className={`group relative shrink-0 snap-start overflow-hidden rounded-2xl ${i === 0 ? "w-[280px]" : "w-[190px]"} h-[150px] cursor-pointer`}>
          <img src={f.url} alt={f.cap} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
          <span className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/5 to-transparent" />
          <figcaption className="absolute left-3 right-3 bottom-2.5 text-[12px] font-medium text-white/95 leading-tight">
            {f.cap}
          </figcaption>
        </figure>
      ))}
    </div>
  </div>
);

/* ───────────── Un solo artículo del muro (común) ───────────── */
const MuroUno = () => (
  <div className="bg-white rounded-[28px] border border-slate-200 overflow-hidden md:flex" data-testid="preview-muro-uno">
    <div className="md:w-[38%] h-48 md:h-auto relative shrink-0">
      <img src={FOTOS[2].url} alt="" className="h-full w-full object-cover" />
      <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.14em] px-2.5 py-1 rounded-full bg-white/95 text-[#ff731d]">
        <Pin className="h-3 w-3" /> Fijado
      </span>
    </div>
    <div className="p-7 md:p-8 flex-1">
      <Etiqueta className="text-slate-400">El Muro · 24 de octubre</Etiqueta>
      <h3 className="mt-2.5 font-display text-2xl font-bold tracking-tight text-slate-900 leading-snug">
        Paseo a Río Celeste: apuntate antes del viernes
      </h3>
      <p className="mt-3 text-[15px] text-slate-600 leading-relaxed">
        Salimos a las 6 a. m. desde la sede de Escazú. El transporte y la entrada van
        incluidos, y practicamos todo el día en español con los profes…
      </p>
      <div className="mt-6 flex items-center gap-4 flex-wrap">
        <button className="rounded-full bg-slate-900 text-white text-sm font-semibold px-6 py-3 inline-flex items-center gap-2 hover:bg-[#004aad] transition-colors">
          Leer la publicación <ArrowRight className="h-4 w-4" />
        </button>
        <span className="text-sm text-slate-500 inline-flex items-center gap-1.5">
          <MessageCircle className="h-4 w-4" /> 12 comentarios
        </span>
        <button className="text-[13px] font-semibold text-[#004aad] ml-auto inline-flex items-center gap-1.5 hover:gap-2.5 transition-all">
          Ver todo el muro <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  </div>
);

const Saludo = () => (
  <div className="mb-8">
    <Etiqueta className="text-[#ff731d]">Pase WSS Live · activo</Etiqueta>
    <h1 className="mt-2.5 font-display text-4xl sm:text-5xl font-bold tracking-[-0.03em] text-slate-900">
      ¡Pura vida, Wagner!
    </h1>
    <p className="mt-2 text-base text-slate-500">
      Tu próxima clase en vivo es <strong className="text-slate-700">hoy a las 5:00 p.m.</strong>
    </p>
  </div>
);

export default function LivePassHomePreview() {
  return (
    <div className="max-w-[1200px]" data-testid="live-pass-home-preview">
      <div className="mb-12">
        <Etiqueta className="text-[#ff731d]">Maqueta · datos de ejemplo</Etiqueta>
        <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
          Panel de inicio del estudiante
        </h1>
        <p className="mt-2 text-sm text-slate-600 max-w-2xl">
          Opción A con solo tres accesos (WSS Live, Conjugación y Recursos), la línea de
          fotografías y un solo artículo del muro. Dos acabados para la misma idea:
          elegí uno y lo armo con los datos reales.
        </p>
      </div>

      <Bloque testId="preview-bloque-color" titulo="Acabado 1 · Tarjetas de color"
              nota="Cada acceso con su propio color de marca, bien visible.">
        <Saludo />
        <Estilo1 />
      </Bloque>

      <Bloque testId="preview-bloque-foto" titulo="Acabado 2 · Tarjetas con foto"
              nota="Blancas y limpias, con una foto arriba y el color como acento.">
        <Estilo2 />
      </Bloque>

      <Bloque testId="preview-bloque-fotos" titulo="Debajo de las tarjetas · La línea de fotografías"
              nota="Se corre con el dedo. Igual para el pase y para los matriculados.">
        <LineaFotos />
      </Bloque>

      <Bloque testId="preview-bloque-muro" titulo="Y de último · Un solo artículo del muro"
              nota="Solo la publicación más reciente o fijada; para ver más, al muro.">
        <MuroUno />
      </Bloque>
    </div>
  );
}
